import { AxiosRequestConfig } from 'axios'
import {
  BaseTableRequest,
  ETimelinePostStatus,
  ETimelinePostType,
  ETimelineSearchType,
  FilterModel,
  RequestById,
  Response,
  TableResponse,
  TimelineEnhancedSearchResult,
  TimelineItem,
  TimelineSearchResult,
  UpdateTimelineItem,
  User,
} from 'types'
import { getFromLocalStorage, USER_DATA } from 'utils'
import axiosInstance from './axios'

export const getTimelines = async (request: BaseTableRequest) => {
  return axiosInstance.post<BaseTableRequest, TableResponse<TimelineItem>>('/timelines', request)
}

export const getTimelineById = async (request: RequestById): Promise<Response<TimelineItem>> => {
  return axiosInstance.post('/timelines/read', request)
}

export const getTimelineItemByEntity = async (
  entityId: number,
  entityType: ETimelinePostType,
  config?: AxiosRequestConfig,
): Promise<Response<TimelineItem>> => {
  return axiosInstance.post(
    '/timelines/read-by-entity',
    {
      entityId,
      type: entityType,
    },
    config,
  )
}

export const setStatus = async (
  request: UpdateTimelineItem,
): Promise<Response<{ timeline: TimelineItem; status: 'created' | 'updated' }>> => {
  return axiosInstance.post('/timelines/status', request)
}

export const searchPostTimeline = async (
  searchText: string,
  filter: FilterModel | null,
  type: ETimelineSearchType,
): Promise<TableResponse<TimelineSearchResult>> => {
  const user = getFromLocalStorage(USER_DATA) as User | null
  return axiosInstance.post('/timelines/search', {
    params: {
      searchText,
      userId: user?.id,
      type,
    },
    filter,
    sort: null,
    pagination: {
      page: 1,
      pageSize: 25,
    },
  })
}

// Global variable for semantic search threshold
declare global {
  interface Window {
    semanticThreshold: number
  }
}

const DEFAULT_SEMANTIC_THRESHOLD = 0.77
window.semanticThreshold = DEFAULT_SEMANTIC_THRESHOLD

export const searchEnhancedTimeline = async (
  searchText: string,
  filter: FilterModel | null,
  type: ETimelineSearchType,
): Promise<TableResponse<TimelineSearchResult>> => {
  const response = await axiosInstance.post('/search/enhanced', {
    searchText,
    contentTypes: ['lesson', 'careArticle', 'learnArticle'],
    diseaseStages: ['early', 'middle', 'late'],
    formats: ['practice', 'theory', 'checklist'],
    semanticThreshold: window.semanticThreshold ?? DEFAULT_SEMANTIC_THRESHOLD,
  })

  const enhancedResult = response.data as TimelineEnhancedSearchResult

  return {
    data: {
      rows: enhancedResult.results.map((item) => ({
        itemType: item.contentType,
        itemId: item.id,
        post: item.title,
        timelineId: null,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        status: ETimelinePostStatus.new,
      })),
      count: enhancedResult.totalCount,
    },
  }
}
